import { useState, useMemo, useEffect, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { Container, Card, PageContainer, PageHeader, Badge, InfoCard, BackButton } from '../../../styles/global'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { StructureCanvas } from './StructureCanvas'
import {
  DEFAULT_SLABS,
  DEFAULT_BEAMS,
  DEFAULT_PILLARS,
  DEFAULT_LOADS,
  DEFAULT_MESH_MAX_SPACING,
  mapSlabsForApi,
  mapBeamsForApi,
  mapPillarsForApi,
  mapLoadsForApi,
} from './model'

export function ConcreteGrelha() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [pillars, setPillars] = useState([])
  const [beams, setBeams] = useState([])
  const [slabs, setSlabs] = useState([])
  const [loads, setLoads] = useState([])
  const [meshMaxSpacing, setMeshMaxSpacing] = useState(DEFAULT_MESH_MAX_SPACING)

  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const beamIds = useMemo(() => beams.map((b) => b.id).filter((id) => id !== ''), [beams])
  const slabIds = useMemo(() => slabs.map((s) => s.id).filter((id) => id !== ''), [slabs])

  const formErrors = useMemo(() => {
    const errors = []

    if (slabs.length === 0) {
      errors.push('Adicione ao menos uma laje')
    }
    if (Number(meshMaxSpacing) <= 0 || isNaN(Number(meshMaxSpacing))) {
      errors.push('Espaçamento máximo da malha inválido')
    }

    const seenPillarIds = new Set()
    pillars.forEach((p, index) => {
      const label = p.id || `#${index + 1}`
      if (p.id === '' || p.id === null || p.id === undefined) {
        errors.push(`Pilar #${index + 1}: id obrigatório`)
      } else if (seenPillarIds.has(p.id)) {
        errors.push(`Pilar "${p.id}" duplicado`)
      } else {
        seenPillarIds.add(p.id)
      }
      if (p.width === '' || isNaN(Number(p.width)) || Number(p.width) <= 0) {
        errors.push(`Pilar "${label}": largura inválida`)
      }
      if (p.depth === '' || isNaN(Number(p.depth)) || Number(p.depth) <= 0) {
        errors.push(`Pilar "${label}": profundidade inválida`)
      }
    })

    const seenBeamIds = new Set()
    beams.forEach((b, index) => {
      const label = b.id || `#${index + 1}`
      if (b.id === '' || b.id === null || b.id === undefined) {
        errors.push(`Viga #${index + 1}: id obrigatório`)
      } else if (seenBeamIds.has(b.id)) {
        errors.push(`Viga "${b.id}" duplicada`)
      } else {
        seenBeamIds.add(b.id)
      }
      if (b.width === '' || isNaN(Number(b.width)) || Number(b.width) <= 0) {
        errors.push(`Viga "${label}": largura inválida`)
      }
      if (b.height === '' || isNaN(Number(b.height)) || Number(b.height) <= 0) {
        errors.push(`Viga "${label}": altura inválida`)
      }
      if (b.E === '' || isNaN(Number(b.E)) || Number(b.E) <= 0) {
        errors.push(`Viga "${label}": E inválido`)
      }
    })

    const seenSlabIds = new Set()
    slabs.forEach((s, index) => {
      const label = s.id || `#${index + 1}`
      if (s.id === '' || s.id === null || s.id === undefined) {
        errors.push(`Laje #${index + 1}: id obrigatório`)
      } else if (seenSlabIds.has(s.id)) {
        errors.push(`Laje "${s.id}" duplicada`)
      } else {
        seenSlabIds.add(s.id)
      }
      if (!Array.isArray(s.polygon) || s.polygon.length < 3) {
        errors.push(`Laje "${label}": polígono precisa de ao menos 3 vértices`)
      }
      if (s.thickness === '' || isNaN(Number(s.thickness)) || Number(s.thickness) <= 0) {
        errors.push(`Laje "${label}": espessura inválida`)
      }
      if (s.E === '' || isNaN(Number(s.E)) || Number(s.E) <= 0) {
        errors.push(`Laje "${label}": E inválido`)
      }
    })

    const seenLoadIds = new Set()
    loads.forEach((l, index) => {
      const label = l.id || `#${index + 1}`
      if (l.id === '' || l.id === null || l.id === undefined) {
        errors.push(`Carga #${index + 1}: id obrigatório`)
      } else if (seenLoadIds.has(l.id)) {
        errors.push(`Carga "${l.id}" duplicada`)
      } else {
        seenLoadIds.add(l.id)
      }
      if (l.type === 'area' && !slabIds.includes(l.slab)) {
        errors.push(`Carga "${label}": laje inválida`)
      }
      if (l.type === 'line' && !beamIds.includes(l.beam)) {
        errors.push(`Carga "${label}": viga inválida`)
      }
      if (l.value === '' || isNaN(Number(l.value))) {
        errors.push(`Carga "${label}": valor inválido`)
      }
    })

    return errors
  }, [pillars, beams, slabs, loads, meshMaxSpacing, beamIds, slabIds])

  const isFormValid = formErrors.length === 0

  function buildPayload() {
    return {
      mesh: { maxSpacing: Number(meshMaxSpacing) || 0 },
      slabs: mapSlabsForApi(slabs),
      beams: mapBeamsForApi(beams),
      pillars: mapPillarsForApi(pillars),
      loads: mapLoadsForApi(loads),
      options: { diagramPoints: 21 },
    }
  }

  async function handleCalculate() {
    setIsLoading(true)

    try {
      const payload = buildPayload()
      setData(payload)

      const response = await api.post('/structural/grid-solver', payload)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error(error.response?.data?.message || 'Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  // Any edit to the model invalidates the computed result, so clear it and let the user
  // recalculate manually. Loading a saved file is exempt: it restores its own saved result, so we
  // mark that model signature as already seen.
  const lastModelSignatureRef = useRef(null)
  const skipNextClearRef = useRef(false)

  useEffect(() => {
    const signature = JSON.stringify([pillars, beams, slabs, loads, meshMaxSpacing])

    if (signature === lastModelSignatureRef.current) return
    const isInitial = lastModelSignatureRef.current === null
    lastModelSignatureRef.current = signature
    if (isInitial) return

    if (skipNextClearRef.current) {
      skipNextClearRef.current = false
      return
    }

    setResult('')
    setData({})
  }, [pillars, beams, slabs, loads, meshMaxSpacing])

  function handleSave() {
    const stateData = { pillars, beams, slabs, loads, meshMaxSpacing, result, data }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Grelha.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        skipNextClearRef.current = true
        setPillars(jsonData.pillars || DEFAULT_PILLARS)
        setBeams(jsonData.beams || DEFAULT_BEAMS)
        setSlabs(jsonData.slabs || DEFAULT_SLABS)
        setLoads(jsonData.loads || DEFAULT_LOADS)
        setMeshMaxSpacing(jsonData.meshMaxSpacing ?? DEFAULT_MESH_MAX_SPACING)
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Grelha</span>
          </div>
          <h1>🔲 Análise de Pavimento por Grelha</h1>
          <p className="subtitle">
            Lance pilares, vigas e lajes de um pavimento e deixe a API discretizar a malha e resolver a estrutura
          </p>
          <div className="badges">
            <Badge>🏗️ Modelo Físico</Badge>
            <Badge>📐 Discretização Automática</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Desenhe os pilares, vigas e lajes do pavimento como em uma planta baixa, e adicione as cargas (de área
              sobre a laje, lineares sobre as vigas, ou pontuais). Você não precisa criar nós, barras, apoios ou cargas
              equivalentes — a API discretiza automaticamente o modelo físico em uma malha de grelha, com o espaçamento
              máximo informado, e resolve a estrutura.
            </p>
            <p>
              O cálculo retorna a malha gerada (nós e elementos), os deslocamentos nodais, as reações de apoio e os
              esforços internos (cortante, momento fletor e torção) ao longo de cada elemento discretizado.
            </p>
            <div className="norms">
              <span>Discretização automática (malha ortogonal)</span>
              <span>Pilares como apoios rígidos</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <p style={{ marginBottom: '10px' }}>
            O solver não converte unidades. Mantenha um sistema consistente em todo o modelo (ex.: kN, m, kN/m², m⁴).
          </p>

          <StructureCanvas
            pillars={pillars}
            beams={beams}
            slabs={slabs}
            loads={loads}
            meshMaxSpacing={meshMaxSpacing}
            setPillars={setPillars}
            setBeams={setBeams}
            setSlabs={setSlabs}
            setLoads={setLoads}
            setMeshMaxSpacing={setMeshMaxSpacing}
            disabled={isLoading}
            result={result}
            data={data}
            formErrors={formErrors}
            isFormValid={isFormValid}
            canCalculate={canCalculate}
            onCalculate={handleCalculate}
            onSave={handleSave}
            onFileUpload={handleFileUpload}
          />
        </Card>
      </Container>
    </PageContainer>
  )
}
